import { createAction } from 'redux-actions';
import { get, pick } from 'lodash';
import { PAGE_TYPES, PAGE_SUB_TYPES } from '@sparefoot/tracking-events';
import { searchResponseSchema } from 'store/schema/search';
import formatSearchFacilities from 'utils/store/formatSearchFacilities';
import { API } from 'store/middleware/api';
import { allowedSearchParams } from 'lib/search';

export const SEARCH = {
	REQUEST: 'SFDC/SEARCH/SEARCH_REQUEST',
	SUCCESS: 'SFDC/SEARCH/SEARCH_SUCCESS',
	FAILURE: 'SFDC/SEARCH/SEARCH_FAILURE',
	DEALS_REQUEST: 'SFDC/SEARCH/SEARCH_REQUEST',
	DEALS_SUCCESS: 'SFDC/SEARCH/SEARCH_SUCCESS',
	DEALS_FAILURE: 'SFDC/SEARCH/SEARCH_FAILURE',
	HIGHLIGHT_FACILITY: 'SFDC/SEARCH/HIGHLIGHT_FACILITY',
	PAGE_REQUEST: 'SFDC/SEARCH/SEARCH_PAGE_REQUEST',
	PAGE_SUCCESS: 'SFDC/SEARCH/SEARCH_PAGE_SUCCESS',
	PAGE_FAILURE: 'SFDC/SEARCH/SEARCH_PAGE_FAILURE',
	SEARCH_LANDING_REQUEST: 'SFDC/SEARCH/SEARCH_LANDING_REQUEST',
	SEARCH_LANDING_SUCCESS: 'SFDC/SEARCH/SEARCH_LANDING_SUCCESS',
	SEARCH_LANDING_FAILURE: 'SFDC/SEARCH/SEARCH_LANDING_FAILURE',
	STORAGE_DEALS_REQUEST: 'SFDC/PAGES/STORAGE_DEALS_REQUEST',
	STORAGE_DEALS_SUCCESS: 'SFDC/PAGES/STORAGE_DEALS_SUCCESS',
	STORAGE_DEALS_FAILURE: 'SFDC/PAGES/STORAGE_DEALS_FAILURE',
	MAP_REDO_SEARCH: 'SFDC/SEARCH/MAP_REDO_SEARCH',
};

export const highlightFacility = createAction(
	SEARCH.HIGHLIGHT_FACILITY,
	(facilityId) => ({ facilityId })
);
export const clearHighlightedFacility = createAction(
	SEARCH.HIGHLIGHT_FACILITY,
	() => ({ facilityId: null })
);

export const mapRedoSearchAction = createAction(
	SEARCH.MAP_REDO_SEARCH,
	(mapRedoSearch) => ({ mapRedoSearch })
);

export function getSearchPage(query) {
	const params = pick(query, allowedSearchParams);

	params.listingsPerPage = 30;

	return {
		[API]: {
			types: [
				SEARCH.PAGE_REQUEST,
				SEARCH.PAGE_SUCCESS,
				SEARCH.PAGE_FAILURE,
			],
			endpoint: '/page/search',
			adapter: formatSearchFacilities,
			schema: searchResponseSchema,
			query: {
				...params,
			},
		},
	};
}

export function getSearchLandingPage(query, meta = {}) {
	const params = pick(query, allowedSearchParams);
	params.listingsPerPage = 30;

	return {
		[API]: {
			types: [
				SEARCH.SEARCH_LANDING_REQUEST,
				SEARCH.SEARCH_LANDING_SUCCESS,
				SEARCH.SEARCH_LANDING_FAILURE,
			],
			endpoint: '/page/searchLanding',
			adapter: formatSearchFacilities,
			schema: searchResponseSchema,
			meta,
			query: {
				...params,
			},
		},
	};
}

export function getCityLandingPage(params, query) {
	return getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			subPageType: 'cityLandingPage',
			...query,
		},
		{ pageType: PAGE_TYPES.CITY_LANDING }
	);
}

export function getVehicleLandingPage(params, query) {
	return getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			searchType: 'vehicle',
			vehicle: params.vehicle,
			subPageType: PAGE_SUB_TYPES.VEHICLE_LANDING,
			...query,
		},
		{ pageType: PAGE_SUB_TYPES.VEHICLE_LANDING }
	);
}

export function getAmenityLandingPage(params, query) {
	return getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			amenity: params.amenity,
			subPageType: PAGE_SUB_TYPES.AMENITY_LANDING,
			...query,
		},
		{ pageType: PAGE_SUB_TYPES.AMENITY_LANDING }
	);
}

export function getNeighborhoodLandingPage(params, query) {
	return getSearchLandingPage(
		{
			city: params.city,
			state: params.state,
			location: `${params.neighborhood || ''}, ${params.city || ''}, ${
				params.state || ''
			}`,
			neighborhood: params.neighborhood || '',
			subPageType: PAGE_SUB_TYPES.NEIGHBORHOOD_LANDING,
			...query,
		},
		{ pageType: PAGE_SUB_TYPES.NEIGHBORHOOD_LANDING }
	);
}

export function getStorageDealsPage(params) {
	return (dispatch, getState) => {
		const { user, app } = getState();
		return dispatch({
			[API]: {
				types: [
					SEARCH.STORAGE_DEALS_REQUEST,
					SEARCH.STORAGE_DEALS_SUCCESS,
					SEARCH.STORAGE_DEALS_FAILURE,
				],
				endpoint: '/page/storageDealsLanding',
				query: {
					searchType: 'storage-only',
					ip: get(user, 'user_ip'),
					...params,
				},
				meta: {
					page: 'storage',
					baseURL: get(app, 'baseUrl', ''),
				},
				adapter: formatSearchFacilities,
				schema: searchResponseSchema,
			},
		});
	};
}

export function fetchStorageDealsResults(query) {
	return (dispatch, getState) => {
		const { app } = getState();
		return dispatch({
			[API]: {
				types: [
					SEARCH.DEALS_REQUEST,
					SEARCH.DEALS_SUCCESS,
					SEARCH.DEALS_FAILURE,
				],
				endpoint: '/search/storage-deals',
				query: pick(query, allowedSearchParams),
				meta: {
					baseURL: get(app, 'baseUrl', ''),
				},
			},
		});
	};
}

export function fetchSearchResults(query) {
	return (dispatch, getState) => {
		const { app } = getState();
		const params = pick(query, allowedSearchParams);

		params.listingsPerPage = 30;

		return dispatch({
			[API]: {
				types: [SEARCH.REQUEST, SEARCH.SUCCESS, SEARCH.FAILURE],
				endpoint: '/search',
				query: {
					...params,
				},
				meta: {
					baseURL: get(app, 'baseUrl', ''),
				},
			},
		});
	};
}

export function fetchSearchPageResults(query) {
	const params = pick(query, allowedSearchParams);

	params.listingsPerPage = 30;

	return {
		[API]: {
			types: [SEARCH.REQUEST, SEARCH.SUCCESS, SEARCH.FAILURE],
			endpoint: '/page/search',
			query: {
				...params,
			},
		},
	};
}

export function getFiveByFiveLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '5x5',
			subPageType: 'city_5x5_landing_page',
			...query,
		},
		{ pageType: 'city_5x5_landing_page' }
	);

	return response;
}

export function getTenByTenLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '10x10',
			subPageType: 'city_10x10_landing_page',
			...query,
		},
		{ pageType: 'city_10x10_landing_page' }
	);

	return response;
}

export function getFiveByTenLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '5x10',
			subPageType: 'city_5x10_landing_page',
			...query,
		},
		{ pageType: 'city_5x10_landing_page' }
	);

	return response;
}

export function getFiveByFifteenLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '5x15',
			subPageType: 'city_5x15_landing_page',
			...query,
		},
		{ pageType: 'city_5x15_landing_page' }
	);

	return response;
}

export function getTenByFifteenLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '10x15',
			subPageType: 'city_10x15_landing_page',
			...query,
		},
		{ pageType: 'city_10x15_landing_page' }
	);

	return response;
}

export function getTenByTwentyLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '10x20',
			subPageType: 'city_10x20_landing_page',
			...query,
		},
		{ pageType: 'city_10x20_landing_page' }
	);

	return response;
}

export function getTenByTwentyFiveLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '10x25',
			subPageType: 'city_10x25_landing_page',
			...query,
		},
		{ pageType: 'city_10x25_landing_page' }
	);

	return response;
}

export function getTenByThirtyLandingPage(params, query) {
	const response = getSearchLandingPage(
		{
			city: params.city || '',
			state: params.state || '',
			location: `${params.city || ''}, ${params.state || ''}`,
			landingPageSize: '10x30',
			subPageType: 'city_10x30_landing_page',
			...query,
		},
		{ pageType: 'city_10x30_landing_page' }
	);

	return response;
}

export function getMiniStorageLandingPage(params, query) {
	const queryParams = {
		...query,
		city: params.city || '',
		state: params.state || '',
		location: `${params.city || ''}, ${params.state || ''}`,
		landingPageSize: 'mini-storage',
		subPageType: 'city_mini_storage_landing_page',
	};

	return getSearchLandingPage(
		queryParams,
		{ pageType: 'city_mini_storage_landing_page' }
	);
}
